import { useCallback, useRef } from "react";

interface UseScrollToLastMessageOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Returns a function that intelligently scrolls to the last message in the chat message list.
 * It will include the previous outgoing message as well if it can be shown along with the last incoming message.
 */
export const useScrollToLastMessage = ({
  containerRef,
}: UseScrollToLastMessageOptions) => {
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const secondToLastMessageRef = useRef<HTMLDivElement | null>(null);
  const outgoingMessageRef = useRef<HTMLDivElement | null>(null);

  const scrollToLastMessage = useCallback(() => {
    const scrollToLast = () => {
      lastMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    if (!outgoingMessageRef.current || !containerRef.current) {
      scrollToLast();
      return;
    }

    // If the last outgoing message is too high, scroll directly to the last message
    if (
      outgoingMessageRef.current.clientHeight >
      containerRef.current.clientHeight - 200
    ) {
      scrollToLast();
      return;
    }

    // If there is more than one incoming message after the last outgoing message, scroll to the last message
    if (secondToLastMessageRef.current !== outgoingMessageRef.current) {
      scrollToLast();
      return;
    }

    // Otherwise include the outgoing message as well
    outgoingMessageRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [containerRef]);

  return {
    refs: {
      lastMessage: lastMessageRef,
      secondToLastMessage: secondToLastMessageRef,
      outgoingMessage: outgoingMessageRef,
    },
    scrollToLastMessage,
  };
};
